import { Component, lazy, Suspense, useEffect, useRef, useState } from "react";

const HeroScene = lazy(() => import("./HeroScene"));

const hasWebGL = () => {
  try {
    const c = document.createElement("canvas");
    const gl = c.getContext("webgl2") || c.getContext("webgl") || c.getContext("experimental-webgl");
    if (!gl) return false;
    gl.getExtension("WEBGL_lose_context")?.loseContext();
    return true;
  } catch {
    return false;
  }
};

class SceneBoundary extends Component {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch() {}
  render() { return this.state.failed ? this.props.fallback : this.props.children; }
}

const COLORS = ["122,92,255", "34,211,238", "255,92,122", "255,255,255"];

export const ParticleField = ({ animate = true }) => {
  const ref = useRef(null);
  useEffect(() => {
    const cv = ref.current;
    const ctx = cv?.getContext("2d");
    if (!ctx) return;
    let w = 0, h = 0, raf = 0, mx = 0, my = 0;
    const dots = Array.from({ length: 140 }, (_, i) => ({
      x: Math.random(), y: Math.random(), r: 0.6 + Math.random() * 1.8,
      vx: (Math.random() - 0.5) * 0.0004, vy: (Math.random() - 0.5) * 0.0004, c: COLORS[i % 4], p: Math.random() * 6.28,
    }));
    const resize = () => { w = cv.width = cv.offsetWidth; h = cv.height = cv.offsetHeight; };
    const move = (e) => { mx = e.clientX / window.innerWidth - 0.5; my = e.clientY / window.innerHeight - 0.5; };
    const draw = (t) => {
      ctx.clearRect(0, 0, w, h);
      for (const d of dots) {
        if (animate) { d.x = (d.x + d.vx + 1) % 1; d.y = (d.y + d.vy + 1) % 1; }
        const a = 0.35 + Math.sin(t / 900 + d.p) * 0.3;
        ctx.beginPath();
        ctx.arc(d.x * w + mx * 30 * d.r, d.y * h + my * 30 * d.r, d.r, 0, 6.28);
        ctx.fillStyle = `rgba(${d.c},${a})`;
        ctx.fill();
      }
      if (animate) raf = requestAnimationFrame(draw);
    };
    resize();
    window.addEventListener("resize", resize);
    window.addEventListener("pointermove", move);
    raf = requestAnimationFrame(draw);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); window.removeEventListener("pointermove", move); };
  }, [animate]);
  return <canvas ref={ref} data-testid="hero-2d-fallback" className="absolute inset-0 h-full w-full" aria-hidden />;
};

export const HeroFallback = ({ animate = true }) => (
  <div data-testid="hero-fallback" className="hero-fallback absolute inset-0" aria-hidden>
    <div className="hero-orb" />
    <div className="hero-ring" />
    <ParticleField animate={animate} />
  </div>
);

export const HeroBackdrop = ({ reduce }) => {
  const [gl, setGl] = useState(null);
  useEffect(() => setGl(!reduce && hasWebGL()), [reduce]);
  if (gl === null) return null;
  const fallback = <HeroFallback animate={!reduce} />;
  if (!gl) return fallback;
  return (
    <SceneBoundary fallback={fallback}>
      <Suspense fallback={null}><HeroScene onFail={() => setGl(false)} /></Suspense>
    </SceneBoundary>
  );
};
